import {
  type IDocumentRef,
  LIFECYCLE_STUDIO_TOOL_TYPES,
  STUDIO_CREATE_FAILURE_REASONS,
  STUDIO_DOCUMENT_TOOL_TYPES,
  STUDIO_PARTIAL_REASONS,
  STUDIO_TOOL_TYPES,
  type StudioCreateFailureReason,
  type StudioPartialReason,
} from '@magicschool/business-logic/studio/client';
import type { Tables } from '@magicschool/supabase/types';
import { isStaticToolUIPart, type ToolUIPart, type UIDataTypes, type UIMessagePart, type UITools } from 'ai';
import type { FileMetadata, InputsType, ToolCustomization } from '../tools/types';
import { isArtifactHandoffPart } from './artifactContract';
import { isSubAgentArtifactPart } from './subAgentContract';
import type {
  DBMessage,
  FEMessage,
  ImageContentItem,
  LoadingContentItem,
  MessageContentItem,
  MessagePayload,
  MessageVersions,
  MinimalContentItem,
  StudioToolPart,
  TextContentItem,
  ToolInputContentItem,
  ToolOutputContentItem,
  WebImageContentItem,
} from './types';

/**
 * Extracts text content from a message content item.
 * - For 'text' type: returns the text field
 * - For 'tool_output' type: returns the output field
 * - For 'tool_inputs' type: returns empty string
 * - For other types: returns empty string
 *
 * @param contentItem - The content item to extract text from
 * @returns The extracted text content or empty string
 */
export function extractTextFromContentItem(contentItem: { type: string; text?: string; output?: string } | undefined | null): string {
  if (!contentItem) return '';

  switch (contentItem.type) {
    case 'text':
      return contentItem.text || '';
    case 'tool_output':
      return contentItem.output || '';
    case 'tool_inputs':
    default:
      return '';
  }
}

/**
 * The tool-form inputs a message carries, wherever the row stores them: v1 rows keep them in the
 * payload metadata, legacy rows as a `tool_inputs` content item, and saved excerpts from before
 * that as `inputs` riding on the first content item whatever its type. The one reader for every
 * shape, so a storage change cannot flip one consumer's answer and not another's.
 */
export function getToolInputs(message: Pick<DBMessage<'v1'>, 'content' | 'message_payload'>): InputsType | undefined {
  const fromPayload = message.message_payload?.metadata?.tool_inputs;
  if (fromPayload) return fromPayload;
  const fromToolInputsItem = message.content?.find(isToolInputContent)?.inputs;
  if (fromToolInputsItem) return fromToolInputsItem;
  return (message.content?.[0] as MinimalContentItem | undefined)?.inputs;
}

/**
 * Extracts tool inputs from the first user message in a list of ThreadMessages
 */
export function extractToolInputsFromMessages(
  messages: Pick<DBMessage<'v1'>, 'content' | 'message_payload' | 'role'>[],
): InputsType | undefined {
  const firstMessage = messages.find((msg) => msg.role === 'user');
  return firstMessage ? getToolInputs(firstMessage) : undefined;
}

/**
 * Extracts document IDs from assistant messages that contain tool call outputs.
 * Looks for createDocument and translateDocument tool calls.
 * - For createDocument/translateDocument: returns documentId to inject full document content
 * - createSubAgent (retired; old threads still hold its parts) and editDocument are intentionally
 *   skipped: the model already has full context for these turns, so injecting any synthetic turn
 *   for them is redundant and gets echoed verbatim by the model on later turns
 *   (createSubAgent: EAS-482; editDocument: CSQD-919)
 *
 * @param messages - Array of messages with message_payload
 * @returns Array of objects containing document IDs and the message indices that introduced them
 */
export function extractDocumentIdsFromMessages(
  messages: Pick<DBMessage<'v1'>, 'role' | 'message_payload'>[],
): Array<{ documentId: string; messageIndex: number; toolType: string }> {
  const documentInfos: Array<{
    documentId: string;
    messageIndex: number;
    toolType: string;
  }> = [];

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (!msg || msg.role !== 'assistant') continue;

    const parts = msg.message_payload?.uiMessage?.parts;
    if (!parts) continue;

    for (const part of parts) {
      if (!isStudioToolPart(part)) continue;

      if (part?.state !== 'output-available') continue;

      const documentId = getStudioPartDocumentId(part);

      if (typeof documentId !== 'string') continue;

      // Skipped per the rationale in the function doc above (createSubAgent: EAS-482; editDocument: CSQD-919).
      if (part.type === STUDIO_TOOL_TYPES.CREATE_SUBAGENT || part.type === STUDIO_TOOL_TYPES.EDIT_DOCUMENT) continue;

      documentInfos.push({
        documentId,
        messageIndex: i,
        toolType: part.type,
      });
    }
  }

  return documentInfos;
}

/**
 * The documents the retired `createSubAgent` tool made earlier in this thread, oldest first. Nothing in model
 * context names them, so callers pass these to the prompt and to `editDocument`'s resolver. A run
 * that failed or never finished is left out: its document is one the client hides.
 */
export function extractThreadPluginDocuments(
  messages: readonly { role: string; message_payload?: Record<string, unknown> | null }[],
): IDocumentRef[] {
  const documents: IDocumentRef[] = [];

  for (const msg of messages) {
    if (msg?.role !== 'assistant') continue;

    const parts = (msg.message_payload as MessagePayload<'v1'> | null | undefined)?.uiMessage?.parts ?? [];
    for (const part of parts) {
      if (!isStudioToolPart(part)) continue;
      if (!isSubAgentArtifactPart(part) || part.aborted === true) continue;

      const documentId = getStudioPartDocumentId(part);
      if (typeof documentId !== 'string') continue;

      const title = (part.output as { title?: unknown } | undefined)?.title;
      if (typeof title !== 'string' || title.trim() === '') continue;

      documents.push({ documentId, title: title.trim() });
    }
  }

  return documents;
}

/**
 * Extracts text content from the first item in a content array.
 * This is a common pattern when accessing message.content[0].
 *
 * @param content - Array of content items
 * @returns The extracted text from the first content item or empty string
 */
export function extractTextFromFirstContent(content: { type: string; text?: string; output?: string }[] | undefined | null): string {
  if (!content || content.length === 0) return '';
  return extractTextFromContentItem(content[0]);
}

/**
 * Extracts and concatenates all text content from an array of content items.
 * Joins multiple text/output items with a specified separator.
 *
 * @param content - Array of content items
 * @param separator - String to join multiple text items (default: ' ')
 * @returns The concatenated text content
 */
export function extractAllTextFromContent(
  content: { type: string; text?: string; output?: string }[] | undefined | null,
  separator = ' ',
): string {
  if (!content || content.length === 0) return '';

  return content
    .map((item) => extractTextFromContentItem(item))
    .filter((text) => text !== '')
    .join(separator);
}

export function isNonBlankString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

type AttachmentText = { name?: unknown; content?: unknown };

export function messageTextWithAttachments(typedText: string, attachments: readonly AttachmentText[] | undefined): string {
  const files = attachmentsWithText(attachments).map(({ name, content }) => (name ? `[${name}]\n${content}` : content));

  return [typedText, ...files].filter(isNonBlankString).join('\n\n');
}

export function messageMarkdownWithAttachments(typedText: string, attachments: readonly AttachmentText[] | undefined): string {
  const files = attachmentsWithText(attachments).map(({ name, content }) => (name ? `**${name}**\n\n${content}` : content));

  return [typedText, ...files].filter(isNonBlankString).join('\n\n---\n\n');
}

function attachmentsWithText(attachments: readonly AttachmentText[] | undefined): { name: string; content: string }[] {
  return (attachments ?? []).flatMap((attachment) =>
    isNonBlankString(attachment?.content)
      ? [{ name: typeof attachment.name === 'string' ? attachment.name : '', content: attachment.content }]
      : [],
  );
}

type TextItemWithFiles = { type: 'text'; text: string; files: readonly AttachmentText[] };

function isTextItemWithFiles(item: unknown): item is TextItemWithFiles {
  if (item === null || typeof item !== 'object') return false;
  const { type, text, files } = item as { type?: unknown; text?: unknown; files?: unknown };
  return type === 'text' && typeof text === 'string' && Array.isArray(files);
}

export function foldAttachmentsIntoMessageText<Row extends { content: unknown }>(row: Row): Row {
  if (!Array.isArray(row.content) || !row.content.some(isTextItemWithFiles)) return row;

  const content = row.content.map((item) =>
    isTextItemWithFiles(item) ? { ...item, text: messageMarkdownWithAttachments(item.text, item.files) } : item,
  );
  return { ...row, content };
}

// Type guards for MessageContentItem types

/**
 * Type guard to check if a content item is a text message
 */
export function isTextContent(item?: MessageContentItem): item is TextContentItem {
  return item?.type === 'text';
}

/**
 * Type guard to check if a content item is a tool input
 */
export function isToolInputContent(item?: MessageContentItem): item is ToolInputContentItem {
  return item?.type === 'tool_inputs';
}

/**
 * Type guard to check if a content item is a tool output
 */
export function isToolOutputContent(item?: MessageContentItem): item is ToolOutputContentItem {
  return item?.type === 'tool_output';
}

/**
 * Type guard to check if a content item is an image
 */
export function isImageContent(item?: MessageContentItem): item is ImageContentItem {
  return item?.type === 'image';
}

/**
 * Type guard to check if a content item is a web image
 */
export function isWebImageContent(item?: MessageContentItem): item is WebImageContentItem {
  return item?.type === 'web-image';
}

export function isStudioToolPart(part: UIMessagePart<UIDataTypes, UITools>): part is StudioToolPart {
  return (
    isStaticToolUIPart(part) &&
    STUDIO_DOCUMENT_TOOL_TYPES.includes(part.type as (typeof STUDIO_DOCUMENT_TOOL_TYPES)[number]) &&
    // A deck handoff minted no document; the `createPresentation` part is the card and the count.
    !isArtifactHandoffPart(part)
  );
}

type StudioPartOutput = {
  documentId?: string;
  metadata?: { documentId?: string };
  generating?: boolean;
  status?: string;
  reason?: string;
  /** The lifecycle tools' discriminator: 'started' until the run emits its 'finished' event. */
  kind?: string;
  error?: string;
  errorReason?: string;
  partial?: boolean;
  partialReason?: string;
  /** Set by the create tool beside `error` when its strategy made nothing and the turn will not retry. */
  failureReason?: string;
  /** Set by the edit tool when it streamed changes to the open editor and then did not save them. */
  discardedStreamedEdit?: boolean;
};

const KNOWN_PARTIAL_REASONS: readonly string[] = Object.values(STUDIO_PARTIAL_REASONS);

const KNOWN_CREATE_FAILURE_REASONS: readonly string[] = Object.values(STUDIO_CREATE_FAILURE_REASONS);

/**
 * Why a `createDocument` made no document, when its strategy refused and the turn will not retry;
 * `null` otherwise. A reason this client does not know reads as `failed`, so a newer server still
 * gets a notice rather than silence.
 */
export function getStudioPartFailureReason(part: StudioToolPart): StudioCreateFailureReason | null {
  if (part.type !== STUDIO_TOOL_TYPES.CREATE_DOCUMENT) return null;
  const output = part.output as StudioPartOutput | undefined;
  if (typeof output?.error !== 'string' || typeof output.failureReason !== 'string') return null;
  return KNOWN_CREATE_FAILURE_REASONS.includes(output.failureReason)
    ? (output.failureReason as StudioCreateFailureReason)
    : STUDIO_CREATE_FAILURE_REASONS.FAILED;
}

/**
 * Returns whether a studio tool part settled with an edit that streamed to the open editor and was
 * then not saved (a refusal, a blank result, a cut-off rewrite), so the editor holds changes the
 * saved row does not and must be replaced from it. Read this, not the reason: the server decides
 * what streamed, and a newer server can discard for a reason this client does not know.
 */
export function hasDiscardedStreamedEdit(part: StudioToolPart): boolean {
  const output = part.output as StudioPartOutput | undefined;
  return Boolean(output?.documentId) && output?.discardedStreamedEdit === true;
}

/**
 * Whether a create's terminal output says it stopped short but kept its content. The document is
 * real and renders; the card says it is incomplete. Read this, not the reason, to decide whether
 * to say so: a newer server can send a reason this client does not know yet.
 */
export function isStudioPartPartial(part: StudioToolPart): boolean {
  return (part.output as StudioPartOutput | undefined)?.partial === true;
}

/**
 * Returns why a partial create stopped short when the reason is one this client knows, or `null`
 * for a complete, failed, or in-flight part and for an unknown reason. Unknown returns `null`
 * rather than a guess, so a newer server cannot make an older client show the wrong explanation;
 * the caller falls back to wording that names no cause.
 */
export function getStudioPartPartialReason(part: StudioToolPart): StudioPartialReason | null {
  if (!isStudioPartPartial(part)) return null;
  const reason = (part.output as StudioPartOutput).partialReason;
  return reason !== undefined && KNOWN_PARTIAL_REASONS.includes(reason) ? (reason as StudioPartialReason) : null;
}

const LIFECYCLE_TOOL_TYPES = new Set<string>(LIFECYCLE_STUDIO_TOOL_TYPES);

const LEGACY_STUDIO_TOOL_TYPES = new Set<string>(STUDIO_DOCUMENT_TOOL_TYPES.filter((t) => !LIFECYCLE_TOOL_TYPES.has(t)));

/**
 * Returns the documentId for a studio tool part regardless of which output convention the tool
 * follows. Legacy studio tools (`createDocument`, `editDocument`, `translateDocument`) put it at
 * `output.documentId`; the retired `createSubAgent`'s stored parts put it under
 * `output.metadata.documentId`. Returns `null` when no documentId is available yet.
 */
export function getStudioPartDocumentId(part: StudioToolPart): string | null {
  const output = part.output as StudioPartOutput | undefined;
  if (!output) return null;
  if (output.documentId) return output.documentId;
  if (part.type === STUDIO_TOOL_TYPES.CREATE_SUBAGENT) {
    return output.metadata?.documentId ?? null;
  }
  return null;
}

/**
 * Returns whether a studio tool part's terminal output reports that the generation failed after
 * the tool had already made its document, so the row exists but the page is not worth showing.
 * The client hides the card; the toast reads only the turn's last document step (`turnEndedWithFailedDocument`).
 *
 * Two output conventions say this. The lifecycle tools (`createArtifact`, and the retired
 * `createSubAgent` in old threads) report `status: 'error'` on a finished event that keeps the
 * `documentId`. The legacy `createDocument` reports an `error` string beside the `documentId` of
 * the document it minted; an `error` without a `documentId` is a model-facing steer yielded before
 * any document existed (validation, consolidation, access denied), which the model retries within the turn and which never rendered anything.
 *
 * `false` for an in-flight or successful part, and for the other legacy tools on purpose. An
 * `editDocument` failure leaves a real document intact, and its part is the one that renders that
 * document's card. A `translateDocument` failure carries the id only when translated content had
 * already streamed to the teacher, so its card is a page they watched arrive and must stay.
 */
export function hasStudioGenerationError(part: StudioToolPart): boolean {
  const output = part.output as StudioPartOutput | undefined;
  if (LIFECYCLE_TOOL_TYPES.has(part.type)) {
    return output?.status === 'error';
  }
  if (part.type === STUDIO_TOOL_TYPES.CREATE_DOCUMENT) {
    return typeof output?.error === 'string' && Boolean(output.documentId);
  }
  return false;
}

/**
 * Returns whether the studio tool is mid-stream.
 *
 * Legacy tools (`createDocument`, `editDocument`, `translateDocument`) historically exposed this
 * via `output.generating`, but the AI SDK frequently coalesces the intermediate `generating: true`
 * yield, so the part can jump straight from `input-available` to the terminal `output-available`
 * with `generating: false` and the client never observes the in-progress flag. We
 * therefore key off the tool-part lifecycle, which is always observed: a legacy tool that has been
 * invoked but has not produced terminal output yet (`input-streaming`/`input-available`) is
 * generating. We additionally honor a non-coalesced preliminary `output-available` chunk and the
 * legacy `output.generating` flag.
 *
 * The lifecycle tools use the AI SDK's `preliminary` marker on `output-available` parts.
 */
export function getStudioPartGenerating(part: StudioToolPart): boolean {
  const output = part.output as StudioPartOutput | undefined;
  if (LEGACY_STUDIO_TOOL_TYPES.has(part.type)) {
    if (part.state === 'input-streaming' || part.state === 'input-available') {
      return true;
    }
    if (part.state === 'output-available') {
      return (part as StudioToolPart & { preliminary?: boolean }).preliminary === true || output?.generating === true;
    }
    return false;
  }
  if (LIFECYCLE_TOOL_TYPES.has(part.type)) {
    return part.state === 'output-available' && (part as StudioToolPart & { preliminary?: boolean }).preliminary === true;
  }
  return false;
}

/**
 * Whether a studio part's run never reached its finish. A stopped turn keeps `output-available`
 * and has `preliminary`/`generating` cleared — `settleAbortedParts` clears them on purpose, so the
 * Studio panel unlocks and autosave resumes — which leaves state, `preliminary` and `generating`
 * all reading exactly as they do on a completed run.
 *
 * Two signals, because each covers what the other misses. `settleAbortedParts` now stamps `aborted`
 * in place of the markers it clears, which is authoritative but only exists on turns stopped since.
 * Rows written before that still carry the evidence in the lifecycle event the output holds:
 * a lifecycle tool writes `kind: 'started'` when the document opens and overwrites it with
 * `kind: 'finished'` only once the run completes, so anything short of `finished` is unfinished.
 * Reading both classifies already-stored threads correctly without a backfill.
 */
export function isUnfinishedStudioPart(part: StudioToolPart): boolean {
  if (part.aborted === true) return true;
  if (part.state !== 'output-available') return false;
  const output = part.output as StudioPartOutput | undefined;
  // Only the lifecycle tools carry `kind`; the legacy tools have no half-finished persisted state.
  return output?.kind === 'started' || output?.kind === 'progress';
}

/**
 * Type guard to check if a content item is any type of image (image or web-image)
 */
export function isImageOrWebImageContent(item: MessageContentItem): item is ImageContentItem | WebImageContentItem {
  return isImageContent(item) || isWebImageContent(item);
}

/**
 * Helper function to find the first image content in a message
 */

export function isLoadingContent(item?: MessageContentItem): item is LoadingContentItem {
  return item?.type === 'loading';
}

export function findImageContent(content: MessageContentItem[] | undefined | null): ImageContentItem | WebImageContentItem | undefined {
  if (!content) return undefined;
  return content.find(isImageOrWebImageContent);
}

/**
 * Extracts files from tool inputs, organizing them by field ID.
 * Returns an array of tuples where first element is field ID and second is array of FileMetadata.
 *
 * @param inputs - The InputsType object containing form field values
 * @returns Array of [fieldId, FileMetadata[]] tuples
 */
export function extractFilesFromInputs(inputs: InputsType): [string, FileMetadata[]][] {
  const filesArray: [string, FileMetadata[]][] = [];

  for (const [fieldName, value] of Object.entries(inputs ?? {})) {
    // Check if the value contains files
    if (value && typeof value === 'object' && 'files' in value && Array.isArray(value.files)) {
      filesArray.push([fieldName, value.files]);
    }
  }

  return filesArray;
}

export function extractFilesFromToolCustomization(toolCustomization: ToolCustomization | undefined | null): [string, FileMetadata[]][] {
  if (!toolCustomization) return [];

  const keys = Object.keys(toolCustomization?.json_config?.inputs || {});
  const values = keys.reduce((acc, key) => {
    const fieldName = key;
    acc[fieldName] = toolCustomization?.json_config?.inputs[key]?.value || '';
    return acc;
  }, {} as InputsType);

  if (toolCustomization?.json_config?.customInstructions) {
    values.customInstructions = toolCustomization.json_config.customInstructions;
  }

  return extractFilesFromInputs(values);
}

export function mapDB<T extends MessageVersions>(message: Tables<'assistant_thread_messages'>): FEMessage<T> {
  if (message.message_payload != null && typeof message.message_payload !== 'object') {
    throw new Error('Invalid message_payload found');
  }

  return {
    ...message,
    content: message.content as unknown as MessageContentItem[],
    message_payload: message.message_payload as unknown as MessagePayload<T>,
  };
}

// biome-ignore lint/suspicious/noExplicitAny: type guard, so it's okay
export function isV1Message(message: any): message is DBMessage<'v1'> {
  return message?.message_payload?.metadata?.message_payload_version === 1;
}

/**
 * Wire type of the balance-and-render `createAssessment` tool's UI part. The
 * client-safe owner of this contract: the server derives the same string from
 * `CREATE_ASSESSMENT_TOOL_NAME` in the server-only `@magicschool/llm` module.
 */
export const ASSESSMENT_TOOL_PART_TYPE = 'tool-createAssessment';

export function isAssessmentToolPart(part: { type: string }): boolean {
  return part.type === ASSESSMENT_TOOL_PART_TYPE;
}

/**
 * Wire type of the `createPresentation` tool's UI part, the client-safe half of the same contract
 * `ASSESSMENT_TOOL_PART_TYPE` owns.
 */
export const PRESENTATION_TOOL_PART_TYPE = 'tool-createPresentation';

/** Wire types of the other two deck tools, client-resolved the same way `createPresentation` is. */
export const EDIT_PRESENTATION_TOOL_PART_TYPE = 'tool-editPresentation';
export const READ_PRESENTATION_TOOL_PART_TYPE = 'tool-readPresentation';

/**
 * Tool parts whose rendered artifact IS the end of the turn: the model has nothing left to say once
 * an assessment is on screen, so the AI SDK's "a completed tool call is waiting to be resumed"
 * heuristic is wrong for it. A deck is not one: the client answers its call when the build
 * settles and the turn resumes on that answer.
 *
 * The retired `createSubAgent`, whose parts old threads still hold, is not one either: it was
 * terminal only when the run succeeded, which this by-type check cannot see.
 */
export function isTerminalArtifactToolPart(part: { type: string }): boolean {
  return part.type === ASSESSMENT_TOOL_PART_TYPE;
}

/** True when the part carries a rendered assessment artifact, not a validation bounce or in-flight input. */
export function isSuccessfulAssessmentToolPart(part: { type: string; state?: string; output?: unknown }): boolean {
  return (
    isAssessmentToolPart(part) &&
    part.state === 'output-available' &&
    typeof part.output === 'object' &&
    part.output !== null &&
    (part.output as { status?: unknown }).status === 'ok'
  );
}

/**
 * The rendered quiz markdown a successful assessment part carries, or undefined
 * for any other part. The one client-safe reader of that payload: the assessment
 * lives only in the tool part, so persistence, exports, and the export preview
 * all need it and must not each reach into `output` on their own.
 */
export function assessmentToolPartMarkdown(part: { type: string; state?: string; output?: unknown }): string | undefined {
  if (!isSuccessfulAssessmentToolPart(part)) return undefined;
  const markdown = (part.output as { markdown?: unknown }).markdown;
  return typeof markdown === 'string' && markdown.length > 0 ? markdown : undefined;
}

export const STEM_GRAPHIC_TOOL_PART_TYPE = 'tool-stemGraphicTool';

type FigurePart = { type: string; toolCallId?: string; input?: unknown; output?: unknown; text?: string };

/** The TikZ a successful stemGraphic part carries, as a fenced block; export surfaces are prose-only. */
export function stemGraphicToolPartMarkdown(part: { type: string; output?: unknown }, label?: string): string | undefined {
  if (part.type !== STEM_GRAPHIC_TOOL_PART_TYPE) return undefined;
  const figure = (part.output as { figure?: unknown } | undefined)?.figure;
  if (typeof figure !== 'string' || figure.trim().length === 0) return undefined;
  const info = label ? `tikz ${label.replace(/[`\s]+/g, ' ').trim()}` : 'tikz';
  return `\`\`\`${info}\n${figure.trim()}\n\`\`\``;
}

export function stemGraphicFigureLabel(parts: readonly FigurePart[], part: FigurePart): string | undefined {
  const index = parts.findIndex((candidate) => candidate === part || (part.toolCallId != null && candidate.toolCallId === part.toolCallId));
  // Only text before the next tool call describes this figure; a later part is about that call.
  const rest = parts.slice(index + 1);
  const nextTool = rest.findIndex((candidate) => candidate.type.startsWith('tool-'));
  const following = rest.slice(0, nextTool === -1 ? undefined : nextTool).find((candidate) => candidate.type === 'text');
  // First paragraph only, without inline markers: the rest of the turn is not a figure description.
  const summary = (following?.text?.trim().split(/\n\s*\n/)[0] ?? '')
    .replace(/^#+\s*/, '')
    .replace(/[*`]/g, '')
    .trim();
  const request = (part.input as { request?: unknown } | undefined)?.request;
  return summary || (typeof request === 'string' ? request : undefined);
}

/** The retired form tool's part type; threads from before its removal still carry it. */
export const CREATE_FORM_TOOL_PART_TYPE = 'tool-createForm';

/** The retired form flow's schema lookup; threads from before its removal still carry it too. */
export const GET_FORM_SCHEMA_TOOL_PART_TYPE = 'tool-getFormSchema';

/** Type guard for a historical `createForm` tool part, narrowing to `ToolUIPart` (which exposes `toolCallId`). */
export function isCreateFormToolPart(part: UIMessagePart<UIDataTypes, UITools>): part is ToolUIPart {
  return isStaticToolUIPart(part) && part.type === CREATE_FORM_TOOL_PART_TYPE;
}

/** Type guard for the Raina `createPresentation` tool part, narrowing to `ToolUIPart`. */
export function isCreatePresentationToolPart(part: UIMessagePart<UIDataTypes, UITools>): part is ToolUIPart {
  return isStaticToolUIPart(part) && part.type === PRESENTATION_TOOL_PART_TYPE;
}

export const ASK_USER_QUESTION_TOOL_PART_TYPE = 'tool-askUserQuestion';

/** Type guard for the Raina `askUserQuestion` tool part, narrowing to `ToolUIPart` (which exposes `toolCallId`). */
export function isAskUserQuestionToolPart(part: UIMessagePart<UIDataTypes, UITools>): part is ToolUIPart {
  return isStaticToolUIPart(part) && part.type === ASK_USER_QUESTION_TOOL_PART_TYPE;
}

/**
 * Tool parts the CLIENT resolves, which is what makes their turn resume: the SDK keeps the halted
 * assistant message and pushes a second one that replays the first's leading parts before
 * continuing. Every one of these needs that replayed prefix stripped, or the turn renders the same
 * narration two or three times with a message-actions toolbar wedged between the copies.
 *
 */
const CLIENT_RESOLVED_TOOL_PART_TYPES = new Set([
  CREATE_FORM_TOOL_PART_TYPE,
  PRESENTATION_TOOL_PART_TYPE,
  EDIT_PRESENTATION_TOOL_PART_TYPE,
  READ_PRESENTATION_TOOL_PART_TYPE,
  ASK_USER_QUESTION_TOOL_PART_TYPE,
]);

/** Whether a tool part type (`tool-<name>`) is one the client resolves. For callers that hold only the wire type. */
export function isClientResolvedToolType(type: string): boolean {
  return CLIENT_RESOLVED_TOOL_PART_TYPES.has(type);
}

/** Type guard for any tool part the client resolves, narrowing to `ToolUIPart` (which exposes `toolCallId`). */
export function isClientResolvedToolPart(part: UIMessagePart<UIDataTypes, UITools>): part is ToolUIPart {
  return isStaticToolUIPart(part) && isClientResolvedToolType(part.type);
}

/** True when the last non-data part is a client-resolved tool call, i.e. the turn is paused on it. */
export function endsWithClientResolvedTool(parts: UIMessagePart<UIDataTypes, UITools>[]): boolean {
  const lastMeaningfulPart = parts.findLast((part) => !part.type.startsWith('data-'));
  return lastMeaningfulPart != null && isClientResolvedToolPart(lastMeaningfulPart);
}

/**
 * True when the last non-data part is a client-resolved call the browser has answered, so the answer
 * has not reached the server yet: a resume the server accepts streams its next step onto this same
 * message, and a failed one leaves it ending here.
 */
export function endsWithUnsentClientAnswer(parts: UIMessagePart<UIDataTypes, UITools>[]): boolean {
  const lastMeaningfulPart = parts.findLast((part) => !part.type.startsWith('data-'));
  return lastMeaningfulPart != null && isClientResolvedToolPart(lastMeaningfulPart) && lastMeaningfulPart.state === 'output-available';
}

/**
 * Concatenates the text of every text part in a message, ignoring tool, data, and file parts.
 *
 * `parts` is optional because callers pass raw request payloads that may omit it.
 */
export function getTextFromParts(message: { parts?: UIMessagePart<UIDataTypes, UITools>[] }): string {
  return (
    message.parts
      ?.filter((part) => part.type === 'text')
      .map((part) => part.text)
      .join('') ?? ''
  );
}
